import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, MapPin, Clock, Shield, Users, Car, ChevronRight, Phone, Mail, Globe, Star,
  CheckCircle2, Building2, Palmtree, Briefcase, Heart, Waves, Mountain
} from "lucide-react";
import PageTransition from "../../components/PageTransition";

const GoldCoastPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Schema markup for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me - Gold Coast",
    "description": "Gold Coast luxury chauffeur services for airport transfers, theme park transport, weddings and corporate travel. Premium fleet with professional chauffeurs.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Gold Coast",
      "addressRegion": "QLD",
      "addressCountry": "AU"
    },
    "telephone": "+61481437772",
    "email": "info@limonearme.com.au",
    "url": "https://www.limonearme.com.au/chauffeur/gold-coast",
    "priceRange": "$$-$$$",
    "openingHours": "Mo-Su 00:00-23:59",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "98"
    },
    "areaServed": {
      "@type": "City",
      "name": "Gold Coast"
    }
  };

  const services = [
    { 
      icon: Plane, 
      title: "Gold Coast Airport Limo Service", 
      desc: "Enjoy hassle-free chauffeur transfers between Gold Coast Airport (OOL), Surfers Paradise, Broadbeach, Coolangatta and beyond."
    },
    { 
      icon: Waves, 
      title: "Theme Park Transfers", 
      desc: "Luxury transport to Dreamworld, Movie World, Sea World, Wet'n'Wild, and all Gold Coast theme parks. Skip the parking hassles."
    },
    { 
      icon: Heart, 
      title: "Wedding & Event Limo Hire", 
      desc: "Make your special day unforgettable with our premium Gold Coast wedding limo service. Beach weddings, hinterland venues, and more."
    },
    { 
      icon: Mountain, 
      title: "Hinterland Tours to Mt. Tamborine", 
      desc: "Choose a limo ride to explore top destinations including Mt. Tamborine, Springbrook National Park, and Gold Coast wineries."
    }
  ];

  const whyChoose = [
    "Fixed pricing and transparent quotes with no hidden costs",
    "Spacious, sanitized vehicles for solo travellers, families, or groups",
    "Professional chauffeurs familiar with Gold Coast attractions",
    "Transfers to local attractions, hotels, resorts, and business venues",
    "Flight tracking with automatic adjustment for delays",
    "24/7 availability for all your transport needs"
  ];

  const destinations = [
    "Surfers Paradise & Main Beach",
    "Broadbeach & Marina Mirage",
    "Hinterland tours to Mt. Tamborine",
    "Dreamworld & WhiteWater World",
    "Movie World, Sea World & Wet'n'Wild",
    "Currumbin Wildlife Sanctuary"
  ];

  const popularRoutes = [
    { from: "Gold Coast Airport (OOL)", to: "Surfers Paradise", price: "From $120" },
    { from: "Gold Coast Airport (OOL)", to: "Broadbeach", price: "From $110" },
    { from: "Brisbane Airport (BNE)", to: "Gold Coast", price: "From $240" },
    { from: "Gold Coast", to: "Theme Parks", price: "From $80" },
    { from: "Gold Coast", to: "Byron Bay", price: "From $220" },
    { from: "Gold Coast", to: "Mt. Tamborine", price: "From $220" }
  ];

  const suburbs = [
    "Surfers Paradise", "Broadbeach", "Main Beach", "Southport", "Burleigh Heads",
    "Coolangatta", "Palm Beach", "Mermaid Beach", "Robina", "Varsity Lakes",
    "Nerang", "Mudgeeraba", "Coomera", "Helensvale", "Hope Island"
  ];

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me Gold Coast" className="h-12 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
              <Link to="/#fleet" className="text-muted-foreground hover:text-foreground transition-colors">Fleet</Link>
              <Link to="/#locations" className="text-muted-foreground hover:text-foreground transition-colors">Locations</Link>
              <Link to="/#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+61481437772" className="hidden sm:flex items-center gap-2 text-primary">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">+61 481 437 772</span>
              </a>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* SEO Helmet */}
      <Helmet>
        <title>Gold Coast Limo Service | Airport Transfers & Theme Park Transport</title>
        <meta name="description" content="Gold Coast luxury chauffeur services for airport transfers, theme park transport, weddings and corporate travel. Premium fleet with professional chauffeurs. Book now." />
        <meta name="keywords" content="Gold Coast limo, Gold Coast chauffeur, Gold Coast airport transfer, theme park transfers, Surfers Paradise limo, OOL airport limo" />
        <link rel="canonical" href="https://www.limonearme.com.au/chauffeur/gold-coast" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop')",
          }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 hero-glow" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">Gold Coast, Queensland</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-6">
              Gold Coast Limo Service<br />
              <span className="text-primary">& Airport Transfers</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Travel the stunning Gold Coast in comfort and sophistication. Our Gold Coast limo services 
              deliver top-tier airport transfers and city travel solutions that match the beauty and energy of the region.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold glow-primary"
                >
                  Get Gold Coast Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg border-black/20 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Benefits of Our Gold Coast Limo Transfers
              </h2>
              <p className="text-muted-foreground mb-6">
                At Limo Near Me, we deliver premium limo and chauffeur services across the Gold Coast. 
                From airport transfers to theme park trips, we've got you covered.
              </p>
              <ul className="space-y-4 mb-8">
                {whyChoose.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Book Gold Coast Limo <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=600&h=400&fit=crop" 
                alt="Gold Coast Limo Service"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Plane className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Gold Coast Airport</p>
                    <p className="text-muted-foreground text-sm">OOL - All Terminals</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Gold Coast Limo & Chauffeur Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Premium limo services for Australia's playground
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card group hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Explore in Style Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <img 
                src="https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&h=400&fit=crop" 
                alt="Gold Coast Theme Parks"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Palmtree className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Explore in Style</p>
                    <p className="text-muted-foreground text-sm">Top Destinations</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Explore Gold Coast in Style
              </h2>
              <p className="text-muted-foreground mb-6">
                Choose a limo ride to explore top Gold Coast destinations. Arrive in style and explore more 
                with Gold Coast's best limo & chauffeur service.
              </p>
              <ul className="space-y-4 mb-8">
                {destinations.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Book Theme Park Transfer <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Gold Coast Limo Transfer Routes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Fixed-price limo transfers to popular Gold Coast destinations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card hover:border-primary/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-foreground font-medium">{route.from}</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-foreground font-medium">{route.to}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-black/10">
                  <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                  <Link to="/book">
                    <Button size="sm" variant="outline" className="rounded-full">
                      Book <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Gold Coast Limo Service Areas</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Limo services from Coolangatta to Coomera and everywhere in between
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3"
          >
            {suburbs.map((suburb, index) => (
              <span 
                key={index}
                className="px-4 py-2 rounded-full bg-card border border-black/10 text-muted-foreground text-sm hover:border-primary/50 hover:text-primary transition-all cursor-default"
              >
                {suburb}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Book Your Gold Coast Limo Today
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Arrive in style and explore more with Gold Coast's best limo & chauffeur service. Get an instant quote today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold glow-primary"
                >
                  Get a Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-10 py-6 text-lg border-primary/50 hover:bg-black/5"
                >
                  <Phone className="mr-2 h-5 w-5" /> +61 481 437 772
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <SeoFooter />
    </div>
    </PageTransition>
  );
};

export default GoldCoastPage;
